import {BasicDataTable, BasicDataTableConfig, TypedColumnSettings} from "@/shared/ui/basicDataTable";
import {RequestParam} from "@/shared/api";
import {RecordingTable} from "@/shared/recording/recordingTable";
import {RecordDetailsDialog, RecordDetailsDialogConfig} from "@/shared/recording/recordDetailsDialog";
import {ValidationsListDialogComponent} from "@/shared/validations/validationsListComponent";
import {htmlEscape} from "@/shared/utils/string";
import {updateCutInfo, updateDeductibleInfo} from "@/shared/utils/misc";
import {formatDate, formatDuration, parseISOZonedDateTime} from "@/shared/utils/datefunctions";
import {isWorkingTimeEditAllowed} from "@/shared/securityHelper";
import {ReportWorkingTime} from "@/shared/timr";
import {DurationUnit, RecordedEntryStatus, WorkingTimeTypeCategory} from "@/shared/timrTypes";

export class WorkingTimeDetailsDialog extends RecordDetailsDialog<ReportWorkingTime> {

	protected workingTimeRevisionTable: WorkingTimeDetailsDialogRevisionTable;

	private readonly validationsList: ValidationsListDialogComponent;

	constructor(config: RecordDetailsDialogConfig<ReportWorkingTime>) {
		super(config);

		if (globals.savedValidationsEnabled) {
			this.validationsList = document.getElementById("workingTimeDetailsValidations") as ValidationsListDialogComponent;
		}
	}

	public show(workingTime: ReportWorkingTime) {
		this.loadRevisionTable(workingTime);
		this.setWorkingTimeFields(workingTime);
		super.show(workingTime);

		this.toggleEditButton(workingTime, isWorkingTimeEditAllowed(workingTime));
		this.updateBorder(workingTime.workingTimeTypeCategory);
		updateDeductibleInfo(this.dom.find("[data-deductible-info]"), workingTime);

		if (globals.savedValidationsEnabled) {
			updateCutInfo(this.dom.find("[data-cut-info]"), workingTime);
			this.validationsList.validationsListItem = {recordId: workingTime.id, validations: workingTime.validations};
		}
	}

	protected setModalTitle(workingTime: ReportWorkingTime) {
		this.dialog.setTitle(htmlEscape(workingTime.workingTimeTypeName));
	}

	protected setRecordFields(record: ReportWorkingTime) {
		const statusValue = `<i class="fal fa-status-${record.status} position-left"></i>` + messages[`reports.filter.status.${record.status}`];
		this.dom.find(".recordDetailsDialogStatus").html(statusValue);
		this.dom.find(".recordDetailsDialogUser").text(record.userName);
		this.dom.find(".recordDetailsDialogDurationGross").text(formatDuration(record.durationGross));
		this.dom.find(".recordDetailsDialogDurationNet").text(formatDuration(record.durationNet));
	}


	/**
	 * Loads the history/revisions of the record
	 * Initializes the table containing this data if not already done
	 * @param {WorkingTime} workingTime
	 * @protected
	 */
	protected loadRevisionTable(workingTime: ReportWorkingTime) {
		if (!this.workingTimeRevisionTable) {
			this.workingTimeRevisionTable = new WorkingTimeDetailsDialogRevisionTable({
				allowExpand: false,
				clampNote: false,
				element: this.dom.find("#workingTimeDetailsDialogRevisionTable"),
				orderable: false,
				initialFilter: WorkingTimeDetailsDialogRevisionTable.buildFilterParams(workingTime.id)
			});
			this.workingTimeRevisionTable.reload();
		} else {
			this.workingTimeRevisionTable.filterByWorkingTimeId(workingTime.id);
		}
	}

	protected setWorkingTimeFields(workingTime: ReportWorkingTime) {
		this.setDescriptionField(workingTime);
		this.setWorkingTimeDurationDaysFields(workingTime);

		this.dom.find("#workingTimeDetailsDialogWorkingTimeType").text(workingTime.workingTimeTypeName);
		this.dom.find(".recordDetailsDialogBreakTime").text(workingTime.breakTime);
	}

	protected setWorkingTimeDurationDaysFields(workingTime: ReportWorkingTime) {
		const durationDays = workingTime.editUnit !== DurationUnit.Minutes;
		this.dom.find(".workingTimeDetailsDialogDurationDayColumn").toggle(durationDays);
		this.dom.find(".workingTimeDetailsDialogDurationMinuteColumn").toggle(!durationDays);

		if (durationDays) {
			const startDate = parseISOZonedDateTime(workingTime.start);
			this.dom.find(".workingTimeDetailsDurationDaysDurationField").text(workingTime.durationDays);
			this.dom.find(".workingTimeDetailsDurationDaysDateField").text(formatDate(startDate));
			this.dom.find(".workingTimeDetailsDurationNet").text(formatDuration(workingTime.durationNet));
		}
	}

	protected updateBorder(workingTimeTypeCategory: WorkingTimeTypeCategory) {
		const modalContent = this.dom.parents("div.modal-content");

		modalContent.removeClass("border-NOT_INITIALIZED");

		Object.keys(WorkingTimeTypeCategory)
			.map(k => WorkingTimeTypeCategory[k as keyof typeof WorkingTimeTypeCategory])
			.forEach(c => {
				modalContent.removeClass("border-" + c);

			});

		if (workingTimeTypeCategory !== null) {
			modalContent.addClass("border-" + workingTimeTypeCategory);
		}
	}

	protected getStatus(record: ReportWorkingTime): RecordedEntryStatus {
		return record.status;
	}
}

export class WorkingTimeDetailsDialogRevisionTable extends BasicDataTable<ReportWorkingTime> {

	constructor(public config: BasicDataTableConfig) {
		super("/timr/reports/getWorkingTimeRevisions.json", config);
	}

	public static buildFilterParams(workingTimeId: number): RequestParam[] {
		return [{
			name: "id",
			value: workingTimeId
		}];
	}

	public filterByWorkingTimeId(workingTimeId: number) {
		return this.filter(WorkingTimeDetailsDialogRevisionTable.buildFilterParams(workingTimeId));
	}

	protected getColumnSettings(): TypedColumnSettings<ReportWorkingTime>[] {
		return [
			{data: "lastModified", orderable: this.config.orderable, render: BasicDataTable.renderDateTimeLocal, visible: true},
			{data: "modifiedByUser", orderable: false, render: BasicDataTable.renderText},
			{data: "workingTimeTypeName", orderable: this.config.orderable, render: BasicDataTable.renderText},
			{
				data: "startPlatform",
				orderable: false,
				render: (d, t, r, m) => BasicDataTable.renderPlatformIcon(d, t, r, m, true),
				className: "slim-icon-column"
			},
			{
				data: "start",
				orderable: false,
				render: BasicDataTable.renderDateTimeWithTimeZone,
				className: "pl-10 pr-10 time-column"
			},
			{
				data: "endPlatform",
				orderable: false,
				render: (d, t, r, m) => BasicDataTable.renderPlatformIcon(d, t, r, m, false),
				className: "slim-icon-column"
			},
			{
				data: "durationGross",
				orderable: false,
				render: RecordingTable.renderDuration,
				className: "text-right",
				width: BasicDataTable.COLUMN_SIZE_BREAKTIME
			},
			{
				data: "breakTime",
				orderable: false,
				render: RecordingTable.renderBreakTime,
				className: "text-right",
				width: BasicDataTable.COLUMN_SIZE_BREAKTIME
			},
			{
				data: "durationNet",
				orderable: false,
				render: BasicDataTable.renderDuration,
				className: "text-right",
				width: BasicDataTable.COLUMN_SIZE_DURATION
			},
			{
				data: "description",
				orderable: false,
				className: "table-data-description",
				render: BasicDataTable.renderDescriptionText
			},
			{
				data: "status",
				orderable: false,
				render: RecordingTable.renderStatus,
				className: "slim-icon-column",
				width: BasicDataTable.COLUMN_SIZE_STATUS_ICON
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[5, "desc"]];
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			pageLength: 4,
			deferLoading: 0,
			dom: `rt`
		};
	}
}
